import type { ResolvedGuide } from "@/document/model/guides";
import type { Rect } from "@/lib/math/rect";

const GUIDE_HIT_PX = 6;

export function hitTestGuide(
  guides: ResolvedGuide[],
  point: { x: number; y: number },
  zoom: number,
): ResolvedGuide | undefined {
  const tolerance = GUIDE_HIT_PX / Math.max(0.0001, zoom);

  for (let index = guides.length - 1; index >= 0; index -= 1) {
    const guide = guides[index];
    if (!guide || !guideNearVisibleSegment(guide, point, tolerance)) {
      continue;
    }

    return guide;
  }

  return undefined;
}

function guideNearVisibleSegment(guide: ResolvedGuide, point: { x: number; y: number }, tolerance: number): boolean {
  if (guide.orientation === "vertical") {
    if (Math.abs(point.x - guide.documentPosition) > tolerance) {
      return false;
    }

    return pointOnSpan(point.y, guide.clipBounds, "vertical");
  }

  if (Math.abs(point.y - guide.documentPosition) > tolerance) {
    return false;
  }

  return pointOnSpan(point.x, guide.clipBounds, "horizontal");
}

function pointOnSpan(value: number, clipBounds: Rect | undefined, axis: "horizontal" | "vertical"): boolean {
  if (!clipBounds) {
    return true;
  }

  const min = axis === "vertical"
    ? Math.min(clipBounds.y, clipBounds.y + clipBounds.height)
    : Math.min(clipBounds.x, clipBounds.x + clipBounds.width);
  const max = axis === "vertical"
    ? Math.max(clipBounds.y, clipBounds.y + clipBounds.height)
    : Math.max(clipBounds.x, clipBounds.x + clipBounds.width);
  return value >= min && value <= max;
}

export function pointerInHorizontalRuler(
  clientY: number,
  canvasTop: number,
  insetTop: number,
  rulerSize: number,
): boolean {
  return clientY >= canvasTop + insetTop && clientY <= canvasTop + insetTop + rulerSize;
}

export function pointerInVerticalRuler(
  clientX: number,
  canvasLeft: number,
  insetLeft: number,
  rulerSize: number,
): boolean {
  return clientX >= canvasLeft + insetLeft && clientX <= canvasLeft + insetLeft + rulerSize;
}

export function guideIntersectsClip(guide: ResolvedGuide, clipBounds: Rect): boolean {
  if (guide.orientation === "vertical") {
    const x = guide.documentPosition;
    const x1 = Math.min(clipBounds.x, clipBounds.x + clipBounds.width);
    const x2 = Math.max(clipBounds.x, clipBounds.x + clipBounds.width);
    return x >= x1 && x <= x2;
  }

  const y = guide.documentPosition;
  const y1 = Math.min(clipBounds.y, clipBounds.y + clipBounds.height);
  const y2 = Math.max(clipBounds.y, clipBounds.y + clipBounds.height);
  return y >= y1 && y <= y2;
}
